import { useEffect, useState } from "react";
import type { ComponentProps } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Button, useNotify } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import {
  clampSadaratDaysBefore,
  clampWhatsappMessageLogRetentionDays,
  timezoneSelectOptions,
  toTenantRuntimeConfigFormState,
  toTenantRuntimeConfigPatch,
  type TenantRuntimeConfigFormState,
} from "./tenant-runtime-settings-form";

const emptyState = (): TenantRuntimeConfigFormState => toTenantRuntimeConfigFormState({});

type CronToggleKey = keyof Pick<
  TenantRuntimeConfigFormState,
  | "bookingSummaryCronEnabled"
  | "contributionReminderCronEnabled"
  | "niyaazReminderCronEnabled"
  | "sabilLedgerCronEnabled"
  | "sadaratAllocationReminderCronEnabled"
  | "razaEventDayOfReminderCronEnabled"
>;

const CRON_TOGGLES: Array<{ key: CronToggleKey; label: string }> = [
  { key: "bookingSummaryCronEnabled", label: "Booking summary cron" },
  { key: "contributionReminderCronEnabled", label: "Contribution reminder cron" },
  { key: "niyaazReminderCronEnabled", label: "Niyaaz contribution reminder cron" },
  { key: "sabilLedgerCronEnabled", label: "Sabil ledger monthly cron" },
  {
    key: "sadaratAllocationReminderCronEnabled",
    label: "Sadarat allocation reminder cron",
  },
  {
    key: "razaEventDayOfReminderCronEnabled",
    label: "Raza event day-of reminder cron",
  },
];

type TenantRuntimeSettingsButtonProps = Partial<ComponentProps<typeof Button>>;

export default function TenantRuntimeSettingsButton(props: TenantRuntimeSettingsButtonProps) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TenantRuntimeConfigFormState>(emptyState);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await callApi({
          location: "tenantRuntimeConfig",
          method: "GET",
        });
        if (cancelled) return;
        setForm(toTenantRuntimeConfigFormState(response.data as TenantRuntimeConfigFormState));
      } catch {
        if (!cancelled) {
          notify("Failed to load runtime settings", { type: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, notify]);

  const setToggle = (key: CronToggleKey | "whatsappEnabled", value: boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await callApi({
        location: "tenantRuntimeConfig",
        method: "PATCH",
        data: toTenantRuntimeConfigPatch(form),
      });
      setForm(toTenantRuntimeConfigFormState(response.data as TenantRuntimeConfigFormState));
      notify("Runtime settings saved", { type: "success" });
      setOpen(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      notify(err?.response?.data?.message || err?.message || "Failed to save settings", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        label="Runtime settings"
        onClick={() => setOpen(true)}
        startIcon={<SettingsIcon />}
        {...props}
      />
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Runtime settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cron enablement, timezone, and WhatsApp settings (not print or calendar prefs). Changes
            apply without a redeploy (cached briefly on the API).
          </Typography>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <Typography variant="subtitle2">WhatsApp</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.whatsappEnabled}
                    onChange={(_, v) => setToggle("whatsappEnabled", v)}
                    color="primary"
                  />
                }
                label="WhatsApp messaging enabled"
              />
              <TextField
                label="WhatsApp sender key"
                size="small"
                value={form.whatsappSenderKey}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, whatsappSenderKey: e.target.value }))
                }
                helperText="Leave blank to clear. Must match Meta sender mapping."
              />
              <TextField
                label="Message log retention (days)"
                type="number"
                size="small"
                value={form.whatsappMessageLogRetentionDays}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setForm((prev) => ({
                    ...prev,
                    whatsappMessageLogRetentionDays: clampWhatsappMessageLogRetentionDays(n),
                  }));
                }}
                inputProps={{ min: 7, max: 365 }}
                helperText="How long to keep outbound template and broadcast recipient logs (7–365)."
              />
              <TextField
                label="WhatsApp bot phone"
                size="small"
                value={form.whatsappBotPhone}
                onChange={(e) => setForm((prev) => ({ ...prev, whatsappBotPhone: e.target.value }))}
                helperText="Display number for miqaat check-in posters (wa.me). E.164 or 10-digit India. Leave blank to clear."
              />
              <TextField
                label="Staff leave apply Flow id"
                size="small"
                value={form.staffLeaveApplyFlowId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, staffLeaveApplyFlowId: e.target.value }))
                }
                helperText="Published Meta Flow id for WhatsApp self-apply. Leave blank to clear."
              />
              <TextField
                label="FMB delivery stop Flow id"
                size="small"
                value={form.fmbDeliveryStopFlowId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, fmbDeliveryStopFlowId: e.target.value }))
                }
                helperText="Published Meta Flow id for WhatsApp thali start/stop. Leave blank to clear."
              />
              <TextField
                label="Miqaat self-check-in Flow id"
                size="small"
                value={form.miqaatSelfCheckinFlowId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, miqaatSelfCheckinFlowId: e.target.value }))
                }
                helperText="Published Meta Flow id for WhatsApp visitor self-scan check-in. Leave blank to clear."
              />
              <TextField
                label="Miqaat family check-in Flow id"
                size="small"
                value={form.miqaatFamilyCheckinFlowId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, miqaatFamilyCheckinFlowId: e.target.value }))
                }
                helperText="Published Meta Flow id for WhatsApp family self-scan check-in. Leave blank to clear."
              />

              <Divider />

              <Typography variant="subtitle2">Timezone</Typography>
              <TextField
                select
                label="Timezone"
                size="small"
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                helperText="Used for sadarat allocation and day-of reminder date windows (not other crons)."
              >
                {timezoneSelectOptions(form.timezone).map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </TextField>

              <Divider />

              <Typography variant="subtitle2">Cron jobs</Typography>
              {CRON_TOGGLES.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={form[key]}
                      onChange={(_, v) => setToggle(key, v)}
                      color="primary"
                    />
                  }
                  label={label}
                />
              ))}

              <TextField
                label="Sadarat reminder days ahead"
                type="number"
                size="small"
                value={form.sadaratAllocationReminderDaysBefore}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setForm((prev) => ({
                    ...prev,
                    sadaratAllocationReminderDaysBefore: clampSadaratDaysBefore(n),
                  }));
                }}
                inputProps={{ min: 1, max: 90 }}
                helperText="Rolling window: events from today through today + N days"
                disabled={!form.sadaratAllocationReminderCronEnabled}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleSave} variant="contained" disabled={loading || saving}>
            {saving ? "Saving…" : "Save"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
